package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyRow
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import kotlinx.coroutines.flow.collect
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val EDITOR_ASSIST_SUGGESTIONS_HEADING_ID = "editor-assist-suggestions-heading"

@Composable
internal fun RankedAssistStream(
    state: TranslationEditorState,
    row: KeyRow,
    onSuggestionInserted: (EditorSuggestionInsertion) -> Unit,
    announcementsEnabled: Boolean = true,
    announcementSessionKey: Long = 0L,
    /**
     * The "generate another" bar, drawn under this section's heading.
     *
     * A slot rather than this composable reaching for the provider state itself: which of the four
     * provider branches applies is `EditorAssistContent`'s decision — it already reads
     * `providersLoading`, `providersUnavailable` and the write capability to make it — and moving
     * that branch in here would put the panel's whole provider story inside the results list.
     */
    providerBar: @Composable () -> Unit = {},
) {
    val proposal = state.proposalsByKey[row.key.id]
    val ranked = state.panels.rankedSuggestions(row)
    val results = rankedAssistResults(proposal, ranked)
    val memory = state.panels.suggestions[row.key.id] ?: Async.Loading
    val proposalPending = state.proposalActions.pending.values.any { request ->
        request.claim.projectSlug == state.projectSlug &&
            request.claim.locale == state.locale &&
            request.claim.keyId == row.key.id
    }
    val generationPending = state.panels.generationStatuses.values.any { status ->
        status is EditorGenerationStatus.Running && status.claim.keyId == row.key.id
    }
    val busy = proposalPending || generationPending || memory == Async.Loading

    Section(
        EditorRankedStreamStyle.toModifier()
            .attr("data-editor-ranked-stream", "true")
            .attr("data-editor-assist-section", "suggestions")
            .attr("aria-labelledby", EDITOR_ASSIST_SUGGESTIONS_HEADING_ID)
            .toAttrs(),
    ) {
        AssistSectionHeading(
            t("editor.editor_assist_results.suggestions", "Suggestions"),
            EDITOR_ASSIST_SUGGESTIONS_HEADING_ID,
        )
        providerBar()
        AssistStreamStatus(
            state = state,
            row = row,
            memory = memory,
            announcementsEnabled = announcementsEnabled,
            announcementSessionKey = announcementSessionKey,
        )
        Div(
            Modifier
                .attr("data-editor-assist-stream-content", "true")
                .attr("aria-busy", busy.toString())
                .toAttrs(),
        ) {
            if (results.isNotEmpty() || memory == Async.Loading) {
                AssistResultsList(
                    state = state,
                    row = row,
                    results = results,
                    loading = memory == Async.Loading,
                    onSuggestionInserted = onSuggestionInserted,
                )
            }
            when (memory) {
                Async.Loading -> Unit
                is Async.Failure -> TranslationMemoryFailure(state, row, memory)
                is Async.Success -> if (ranked.isEmpty() && proposal == null) {
                    EditorUtilityPanelEmpty(
                        name = "assist",
                        title = t("editor.editor_assist_results.no_suggestions_yet", "No suggestions yet"),
                        // "Matches appear here when available" is a promise a plural key cannot
                        // keep: a translation-memory match is one flat string with no category to
                        // put it in, so none is ever offered here. Said plainly, the reader learns
                        // the search is impossible rather than waiting on one that already ran.
                        description = when {
                            !state.capability.canWriteTranslations -> if (row.key.isPlural) {
                                t(
                                    "editor.editor_assist_results.agent_proposals_appear_here",
                                    "Agent proposals appear here when one is made for this key.",
                                )
                            } else {
                                t(
                                    "editor.editor_assist_results.translation_memory_matches_appear_here",
                                    "Translation-memory matches appear here when available.",
                                )
                            }

                            state.enabledProviders.isEmpty() -> if (row.key.isPlural) {
                                t(
                                    "editor.editor_assist_results.keep_writing_a_plural_key_takes_no_match",
                                    "Keep writing; a plural key takes no translation-memory match.",
                                )
                            } else {
                                t(
                                    "editor.editor_assist_results.keep_writing_translation_memory_matches",
                                    "Keep writing; translation-memory matches appear here when available.",
                                )
                            }

                            else -> t(
                                "editor.editor_assist_results.choose_a_provider_and_generate",
                                "Choose a provider and generate a suggestion, or write your own version.",
                            )
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun AssistStreamStatus(
    state: TranslationEditorState,
    row: KeyRow,
    memory: Async<*>,
    announcementsEnabled: Boolean,
    announcementSessionKey: Long,
) {
    val activePublicationIds = buildList {
        if (memory == Async.Loading) {
            state.panels.activeSuggestionStatusPublicationId(row.key.id)?.let(::add)
        }
        state.panels.generationStatuses.values.forEach { status ->
            if (
                status is EditorGenerationStatus.Running &&
                status.claim.projectSlug == state.projectSlug &&
                status.claim.locale == state.locale &&
                status.claim.keyId == row.key.id
            ) {
                add(status.sequence)
            }
        }
        state.proposalActions.pending.values.forEach { request ->
            if (
                request.claim.projectSlug == state.projectSlug &&
                request.claim.locale == state.locale &&
                request.claim.keyId == row.key.id
            ) {
                add(request.statusPublicationId)
            }
        }
    }
    var message = ""
    var announcementKey = ""
    var liveRegionReady = false
    if (announcementsEnabled) {
        val session = remember(
            state.assistStatuses,
            state.projectSlug,
            state.locale,
            row.key.id,
            announcementSessionKey,
        ) {
            state.assistStatuses.registerSession(
                projectSlug = state.projectSlug,
                locale = state.locale,
                keyId = row.key.id,
                presenceGeneration = announcementSessionKey,
                activePublicationIds = activePublicationIds,
            )
        }
        var committedMessage by remember(session) { mutableStateOf("") }
        var committedAnnouncementKey by remember(session) { mutableStateOf("") }
        var pendingPublications by remember(session) {
            mutableStateOf<List<EditorAssistStatusPublication>>(emptyList())
        }
        var collectorReady by remember(session) { mutableStateOf(false) }

        DisposableEffect(state, session) {
            state.acquireAssistTerminalFeedback(session)
            onDispose { state.releaseAssistTerminalFeedback(session) }
        }
        LaunchedEffect(
            state.assistStatuses,
            session,
        ) {
            fun publishQueuedMessages() {
                val queued = state.assistStatuses.consumeUnannounced(session)
                if (queued.isNotEmpty()) {
                    pendingPublications = (pendingPublications + queued)
                        .distinctBy(EditorAssistStatusPublication::publicationId)
                        .sortedBy(EditorAssistStatusPublication::publicationId)
                }
            }
            snapshotFlow {
                state.assistStatuses.publicationsFor(
                    state.projectSlug,
                    state.locale,
                    row.key.id,
                )
            }.collect {
                publishQueuedMessages()
                // The first snapshot is delivered only after snapshotFlow has installed its
                // observation boundary. Exposing readiness here lets the browser and production
                // evidence tooling distinguish "the live node exists" from "it can receive the
                // action which is about to publish" without sleeping for an arbitrary frame.
                collectorReady = true
            }
        }
        liveRegionReady = collectorReady
        if (pendingPublications.isNotEmpty()) {
            val committedPublications = pendingPublications
            val committedIds = committedPublications.map(EditorAssistStatusPublication::publicationId)
            val renderedMessage = committedPublications.joinToString(separator = " ") { publication ->
                publication.message
            }
            val renderedAnnouncementKey = committedIds.joinToString(separator = "-")
            message = renderedMessage
            announcementKey = renderedAnnouncementKey
            SideEffect {
                state.assistStatuses.commitAnnouncement(session, committedIds)
                committedMessage = renderedMessage
                committedAnnouncementKey = renderedAnnouncementKey
                pendingPublications = pendingPublications.filterNot { publication ->
                    publication.publicationId in committedIds
                }
            }
        } else {
            message = committedMessage
            announcementKey = committedAnnouncementKey
        }
    }

    Span(
        LVisuallyHiddenStyle.toModifier()
            .attr("data-editor-assist-stream-status", "true")
            .attr("data-editor-assist-stream-ready", liveRegionReady.toString())
            .toAttrs {
                if (announcementsEnabled) {
                    attr("role", "status")
                    attr("aria-live", "polite")
                    attr("aria-atomic", "true")
                } else {
                    attr("aria-hidden", "true")
                }
            },
    ) {
        if (message.isNotEmpty()) {
            key(announcementKey) {
                Span(
                    Modifier
                        .attr("data-editor-assist-announcement", announcementKey)
                        .toAttrs(),
                ) {
                    Text(message)
                }
            }
        }
    }
}

@Composable
private fun AssistResultsList(
    state: TranslationEditorState,
    row: KeyRow,
    results: List<EditorAssistResult>,
    loading: Boolean,
    onSuggestionInserted: (EditorSuggestionInsertion) -> Unit,
) {
    Ol(
        Modifier
            .attr("data-editor-assist-results", "true")
            .attr("role", "list")
            .toAttrs(),
    ) {
        results.forEach { result ->
            when (result) {
                is EditorAssistResult.Proposal -> ProposalAssistItem(state, row, result)
                is EditorAssistResult.Suggestion -> SuggestionAssistItem(
                    state = state,
                    row = row,
                    result = result,
                    onSuggestionInserted = onSuggestionInserted,
                )
            }
        }
        if (loading) {
            repeat(if (results.isEmpty()) 2 else 1) { SuggestionSkeleton() }
        }
    }
}

@Composable
private fun TranslationMemoryFailure(
    state: TranslationEditorState,
    row: KeyRow,
    failure: Async.Failure,
) {
    val copy = translationMemoryFailureMessage(failure.message)
    Div(Modifier.attr("data-editor-assist-tm-failure", "true").toAttrs()) {
        Span(Modifier.attr("data-editor-assist-error-copy", "true").toAttrs()) {
            Text(copy)
        }
        LButton(
            label = t("editor.editor_assist_results.retry", "Retry"),
            onClick = { state.panels.retryAssist(row, failure) },
            modifier = Modifier.editorAssistWrappingAction(),
            variant = LButtonVariant.GHOST,
            size = LButtonSize.SM,
            accessibleLabel = t(
                "editor.editor_assist_results.retry_translation_memory_suggestions",
                "Retry translation-memory suggestions",
            ),
        )
    }
}

@Composable
private fun SuggestionSkeleton() {
    val palette = ColorMode.current.toSitePalette()
    Li(
        Modifier
            .attr("data-editor-assist-skeleton", "true")
            .attr("aria-hidden", "true")
            .toAttrs(),
    ) {
        Div(
            Modifier
                .width(100.percent)
                .height(18.px)
                .backgroundColor(palette.surfaceSunken)
                .borderRadius(Radius.Control)
                .toAttrs(),
        )
        Div(
            Modifier
                .width(72.percent)
                .height(18.px)
                .backgroundColor(palette.surfaceSunken)
                .borderRadius(Radius.Control)
                .toAttrs(),
        )
        Div(
            Modifier
                .width(42.percent)
                .height(12.px)
                .backgroundColor(palette.surfaceSunken)
                .borderRadius(Radius.Control)
                .toAttrs(),
        )
    }
}

internal fun assistResultActionId(shortcutNumber: Int, action: String): String =
    "editor-assist-result-$shortcutNumber-$action"
